// Unified file count utility that ensures consistency between navigation and My Files page

export interface FileCountResult {
  count: number;
  source: "server" | "localStorage" | "none";
  debug: {
    sessionId: string | null;
    keysChecked: string[];
    serverResponse?: any;
  };
}

export class UnifiedFileCount {
  private static readonly SESSION_KEYS = [
    "session_id",
    "docpose_guest_session",
    "guestSessionId", // Legacy key for migration support
  ];
  private static forceLocalStorageMode = false;

  // Enable localStorage-only mode (for debugging or persistent network issues)
  static enableLocalStorageMode() {
    this.forceLocalStorageMode = true;
    console.log("UnifiedFileCount - Enabled localStorage-only mode");
  }

  // Disable localStorage-only mode
  static disableLocalStorageMode() {
    this.forceLocalStorageMode = false;
    console.log("UnifiedFileCount - Disabled localStorage-only mode");
  }

  // Get current session ID (check all possible keys)
  static getCurrentSessionId(): string | null {
    for (const key of this.SESSION_KEYS) {
      const sessionId = localStorage.getItem(key);
      if (sessionId) {
        console.log(
          `UnifiedFileCount - Found session ID: ${sessionId} (key: ${key})`,
        );
        return sessionId;
      }
    }
    console.log("UnifiedFileCount - No session ID found");
    return null;
  }

  // XMLHttpRequest-based safe fetch to avoid browser extension interference
  private static safeFetch(
    url: string,
    headers: Record<string, string> = {},
  ): Promise<{ ok: boolean; data?: any; status?: number }> {
    return new Promise((resolve) => {
      let isResolved = false;

      const timeoutId = setTimeout(() => {
        if (!isResolved) {
          isResolved = true;
          console.warn("UnifiedFileCount - Request timed out");
          resolve({ ok: false, status: 408 });
        }
      }, 3000);

      try {
        const xhr = new XMLHttpRequest();
        xhr.timeout = 3000;

        xhr.onload = () => {
          if (isResolved) return;
          isResolved = true;
          clearTimeout(timeoutId);

          try {
            if (xhr.status >= 200 && xhr.status < 300) {
              const data = JSON.parse(xhr.responseText);
              resolve({ ok: true, data, status: xhr.status });
            } else {
              resolve({ ok: false, status: xhr.status });
            }
          } catch (error) {
            resolve({ ok: false, status: xhr.status || 500 });
          }
        };

        xhr.onerror = () => {
          if (isResolved) return;
          isResolved = true;
          clearTimeout(timeoutId);
          resolve({ ok: false, status: 0 });
        };

        xhr.ontimeout = () => {
          if (isResolved) return;
          isResolved = true;
          clearTimeout(timeoutId);
          resolve({ ok: false, status: 408 });
        };

        xhr.open("GET", url, true);

        // Set headers
        Object.entries(headers).forEach(([key, value]) => {
          xhr.setRequestHeader(key, value);
        });

        xhr.send();
      } catch (error) {
        if (isResolved) return;
        isResolved = true;
        clearTimeout(timeoutId);
        console.warn("UnifiedFileCount - XHR error:", error);
        resolve({ ok: false, status: 0 });
      }
    });
  }

  // Get file count from server (using XMLHttpRequest to avoid fetch issues)
  static async getServerFileCount(): Promise<{
    count: number;
    batches: any[];
  } | null> {
    // Immediately return null if we're in an unstable environment
    if (typeof window === "undefined") {
      return null;
    }

    return new Promise((resolve) => {
      let isResolved = false;

      const attemptFetch = async () => {
        try {
          const token = localStorage.getItem("auth_token");
          const sessionId = this.getCurrentSessionId();

          const headers: Record<string, string> = {
            "Content-Type": "application/json",
          };

          if (token) {
            headers["Authorization"] = `Bearer ${token}`;
          } else if (sessionId) {
            headers["X-Session-Id"] = sessionId;
          }

          console.log(
            "UnifiedFileCount - Making server request with headers:",
            headers,
          );

          const response = await this.safeFetch("/api/files/user", headers);

          if (isResolved) return;

          if (response.ok && response.data) {
            console.log("UnifiedFileCount - Server response:", response.data);

            const batches = response.data.data?.batches || [];
            const totalFiles = batches.reduce((sum: number, batch: any) => {
              return (
                sum +
                (batch.files?.filter((f: any) => f.status === "completed")
                  .length || 0)
              );
            }, 0);

            if (!isResolved) {
              isResolved = true;
              resolve({ count: totalFiles, batches });
            }
          } else {
            console.log(
              "UnifiedFileCount - Server request failed:",
              response.status,
            );
            if (!isResolved) {
              isResolved = true;
              resolve(null);
            }
          }
        } catch (error) {
          if (isResolved) return;

          console.warn("UnifiedFileCount - Server request failed:", error);

          if (!isResolved) {
            isResolved = true;
            resolve(null);
          }
        }
      };

      // Execute the fetch attempt with error isolation
      attemptFetch().catch((error) => {
        if (!isResolved) {
          isResolved = true;
          console.warn("UnifiedFileCount - Fetch attempt failed:", error);
          resolve(null);
        }
      });
    });
  }

  // Get file count from localStorage
  static getLocalStorageFileCount(): { count: number; keysChecked: string[] } {
    const keysChecked: string[] = [];
    let totalCount = 0;

    console.log("UnifiedFileCount - Checking localStorage...");

    // Check all localStorage keys for file data
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;

      // Look for keys that contain file data
      if (key.includes("_files") || key.includes("_batches")) {
        keysChecked.push(key);
        const value = localStorage.getItem(key);

        if (value) {
          try {
            const data = JSON.parse(value);
            console.log(`UnifiedFileCount - Checking key: ${key}`, data);

            if (key.includes("_files") && Array.isArray(data)) {
              // Count completed files
              const completedFiles = data.filter(
                (file) => file.status === "completed",
              );
              totalCount += completedFiles.length;
              console.log(
                `UnifiedFileCount - Key ${key} has ${completedFiles.length} completed files`,
              );
            } else if (key.includes("_batches") && Array.isArray(data)) {
              // Count completed files from batches
              data.forEach((batch) => {
                if (batch.files && Array.isArray(batch.files)) {
                  const completedFiles = batch.files.filter(
                    (file: any) => file.status === "completed",
                  );
                  totalCount += completedFiles.length;
                  console.log(
                    `UnifiedFileCount - Batch ${batch.id} has ${completedFiles.length} completed files`,
                  );
                }
              });
            }
          } catch (error) {
            console.warn(`UnifiedFileCount - Error parsing key ${key}:`, error);
          }
        }
      }
    }

    console.log(`UnifiedFileCount - Total localStorage count: ${totalCount}`);
    return { count: totalCount, keysChecked };
  }

  // Get unified file count (tries server first, falls back to localStorage)
  // This method NEVER throws - it always returns a valid result
  static async getUnifiedFileCount(): Promise<FileCountResult> {
    return new Promise((resolve) => {
      const sessionId = this.getCurrentSessionId();

      // Create a safe default result
      const safeDefault: FileCountResult = {
        count: 0,
        source: "none",
        debug: {
          sessionId,
          keysChecked: [],
        },
      };

      const attemptCount = async () => {
        // Skip server if in localStorage-only mode
        if (!this.forceLocalStorageMode) {
          try {
            // Try server first, but don't let it throw errors
            const serverResult = await this.getServerFileCount();

            if (serverResult !== null) {
              console.log(
                "UnifiedFileCount - Using server data:",
                serverResult.count,
              );
              resolve({
                count: serverResult.count,
                source: "server",
                debug: {
                  sessionId,
                  keysChecked: [],
                  serverResponse: serverResult,
                },
              });
              return;
            }
          } catch (error) {
            // Server failed, continue to localStorage
            console.warn(
              "UnifiedFileCount - Server failed, using localStorage:",
              error.message || error,
            );
          }
        } else {
          console.log(
            "UnifiedFileCount - Skipping server (localStorage-only mode)",
          );
        }

        // Fall back to localStorage
        try {
          const localResult = this.getLocalStorageFileCount();
          console.log(
            "UnifiedFileCount - Using localStorage data:",
            localResult.count,
          );

          resolve({
            count: localResult.count,
            source: localResult.count > 0 ? "localStorage" : "none",
            debug: {
              sessionId,
              keysChecked: localResult.keysChecked,
            },
          });
        } catch (error) {
          // Even localStorage failed, return safe default
          console.debug(
            "UnifiedFileCount - Both server and localStorage failed:",
            error.message || error,
          );
          resolve({
            ...safeDefault,
            debug: {
              ...safeDefault.debug,
              serverResponse: { error: error.message || "Unknown error" },
            },
          });
        }
      };

      // Execute with additional safety
      try {
        attemptCount();
      } catch (error) {
        // Final safety net
        console.debug(
          "UnifiedFileCount - Critical error in getUnifiedFileCount:",
          error,
        );
        resolve(safeDefault);
      }
    });
  }

  // Clear all file-related localStorage data
  static clearAllFileData(): void {
    console.log("UnifiedFileCount - Clearing all file data...");

    const keysToRemove: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (
        key &&
        (key.includes("_files") ||
          key.includes("_batches") ||
          key.includes("guest_") ||
          key.includes("session_") ||
          key.includes("docpose"))
      ) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => {
      localStorage.removeItem(key);
      console.log(`UnifiedFileCount - Removed key: ${key}`);
    });

    console.log(
      `UnifiedFileCount - Cleared ${keysToRemove.length} localStorage keys`,
    );

    // Trigger file count update event (with slight delay to prevent loops)
    if (typeof window !== "undefined") {
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent("fileCountUpdate"));
      }, 100);
    }
  }
}

// Export to window for debugging
if (typeof window !== "undefined") {
  (window as any).UnifiedFileCount = UnifiedFileCount;
  (window as any).clearAllFileData = () => UnifiedFileCount.clearAllFileData();
  (window as any).getUnifiedFileCount = () =>
    UnifiedFileCount.getUnifiedFileCount();
  (window as any).enableLocalStorageMode = () =>
    UnifiedFileCount.enableLocalStorageMode();
  (window as any).disableLocalStorageMode = () =>
    UnifiedFileCount.disableLocalStorageMode();
}
